import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box } from '@cbhq/cds-web/layout';
import { FullscreenModal } from '@cbhq/cds-web/overlays';
import { useToast } from '@cbhq/cds-web/overlays/useToast';

import CloudLogoWordmark from ':cloud/assets/Brand/CDPLogoWordmark';
import useLocalStorage from ':cloud/hooks/useLocalStorage';
import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetProducts } from ':cloud/queries/BillingQueries/useGetProducts';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import { BillingMeter, NewProjectFormContext } from ':cloud/types/ts_types';
import { AppRoute } from ':cloud/utils/routes';
import { NewProjectDetails } from ':cloud/widgets/waasProjects/NewProjectDetails';
import { NewProjectRightRail } from ':cloud/widgets/waasProjects/NewProjectRightRail';
import { ProjectPaymentIntake } from ':cloud/widgets/waasProjects/ProjectPaymentIntake';

type NewProjectMainContentProps = {
  meter?: BillingMeter;
  unitType: string;
};

function NewProjectMainContent({ meter, unitType }: NewProjectMainContentProps) {
  const { isDesktop } = useSimpleBreakpoints();

  const { activeOrg } = useGetUser();
  const organizationId = activeOrg?.organizationId || '';

  // This is used to save & restore form state if the user navigates away
  // e.g. to add/update a payment method, or for a Stripe redirect.
  const [formContext, setFormContext] = useLocalStorage<NewProjectFormContext>(
    'waas-form-context',
    {
      projectName: '',
      projectNameError: '',
      sanctionedAffiliations: undefined,
    },
  );
  const [isPaymentIntake, setIsPaymentIntake] = useState(false);
  const showProjectPaymentIntake = () => {
    setIsPaymentIntake(true);
  };
  const showProjectDetails = () => {
    setIsPaymentIntake(false);
  };

  return (
    <Box flexDirection={isDesktop ? 'row' : 'column'}>
      {isPaymentIntake ? (
        <ProjectPaymentIntake
          onCancel={showProjectDetails}
          onSuccess={showProjectDetails}
          formContext={formContext}
        />
      ) : (
        <NewProjectDetails
          meter={meter}
          organizationId={organizationId}
          unitType={unitType}
          onReplaceCardClick={showProjectPaymentIntake}
          onCancelReplaceCard={showProjectDetails}
          saveFormContext={setFormContext}
          formContext={formContext}
        />
      )}
    </Box>
  );
}

const PRODUCT_SERVICE = 'waas';

export function NewProject() {
  const toast = useToast();
  const history = useHistory();
  const {
    user: { isOwnerOrAdmin },
  } = useGetUser();
  const onModalClosePress = useCallback(() => {
    localStorage.removeItem('waas-form-context');
    history.go(-1);
  }, [history]);

  const { products, isLoading: isProductsLoading } = useGetProducts();

  const displayedMeter = useMemo(() => {
    const currentProduct = products.find((product) => product.serviceId === PRODUCT_SERVICE);
    const activeVariant = currentProduct?.variants.find((variant) => variant.active);
    return activeVariant?.meters?.[0];
  }, [products]);

  useEffect(() => {
    if (!isOwnerOrAdmin) {
      history.push(AppRoute.Home);
      toast.show(`Your permissions are view only.`, {
        action: {
          label: 'Learn More',
          onPress: () => history.push(AppRoute.Access.Permissions),
        },
      });
    }
  }, [history, isOwnerOrAdmin, toast]);

  return (
    <FullscreenModal
      visible
      showSecondaryContentDivider
      logo={<CloudLogoWordmark />}
      onRequestClose={onModalClosePress}
      primaryContent={
        <NewProjectMainContent meter={displayedMeter} unitType={displayedMeter?.type || ''} />
      }
      secondaryContent={<NewProjectRightRail isLoading={isProductsLoading} />}
      title="Create project"
    />
  );
}
